import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { translateOutsideReact } from '@/i18n/translateOutsideReact';
import { handleError } from '@/utils/handleError';

/**
 * Response from connectToErp action
 */
export interface ConnectToErpResponse {
  url: string;
  provider: string;
}

/**
 * Response from getAvailableCompanies action
 */
export interface AvailableCompaniesResponse {
  sessionId: string;
  companies: Array<{ id: string; name: string }>;
}

/**
 * Response from completeConnection action
 */
export interface CompleteConnectionResponse {
  success: boolean;
  message: string;
  companyId?: string;
  companyName?: string;
  salesInvoices?: number;
  vendorInvoices?: number;
  errors?: string[];
}

/**
 * Response from fetchLatestData action
 */
export interface FetchLatestDataResponse {
  success: boolean;
  message: string;
  companyId?: string;
  companyName?: string;
  salesInvoices?: number;
  vendorInvoices?: number;
  errors?: string[];
}

/**
 * Response from getExtensionDownload action
 */
export interface ExtensionDownloadResponse {
  url: string;
  filename: string;
  version: string;
}

/**
 * Client for interacting with the erp-connection-api edge function
 */
export class ErpConnectionApiClient {
  /**
   * Connect to ERP system (currently Business Central)
   * @returns Promise with authorization URL
   */
  static async connectToErp(): Promise<ConnectToErpResponse> {
    try {
      const { data, error } = await supabase.functions.invoke('erp-connection-api', {
        method: 'POST',
        body: { action: 'connectToErp' }
      });

      if (error) throw error;

      toast.success(translateOutsideReact('consultantToasts.api.erpInitiated'));
      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToConnectToErp'), 'connectToErp');
      throw error;
    }
  }

  /**
   * Fetch latest data from ERP system
   * Company ID is discovered automatically from the authenticated user's access
   * @param code - OAuth authorization code
   * @returns Promise with fetch result including discovered company
   */
  static async fetchLatestData(code: string): Promise<FetchLatestDataResponse> {
    try {
      const { data, error } = await supabase.functions.invoke('erp-connection-api', {
        method: 'POST',
        body: {
          action: 'fetchLatestData',
          code
        }
      });

      if (error) throw error;

      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchErpData'), 'fetchLatestData');
      throw error;
    }
  }

  /**
   * Get available companies from ERP (Step 1 of new flow)
   * @param code - OAuth authorization code
   * @returns Promise with session ID and list of companies
   */
  static async getAvailableCompanies(code: string): Promise<AvailableCompaniesResponse> {
    try {
      const { data, error } = await supabase.functions.invoke('erp-connection-api', {
        method: 'POST',
        body: {
          action: 'getAvailableCompanies',
          code
        }
      });

      if (error) throw error;

      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToGetAvailableCompanies'), 'getAvailableCompanies');
      throw error;
    }
  }

  /**
   * Complete ERP connection with selected company (Step 2 of new flow)
   * @param sessionId - Session ID from getAvailableCompanies
   * @param companyId - Selected Business Central company ID
   * @returns Promise with connection result
   */
  static async completeConnection(sessionId: string, companyId: string): Promise<CompleteConnectionResponse> {
    try {
      const { data, error } = await supabase.functions.invoke('erp-connection-api', {
        method: 'POST',
        body: {
          action: 'completeConnection',
          sessionId,
          companyId
        }
      });

      if (error) throw error;

      toast.success(translateOutsideReact('consultantToasts.api.erpCompleted'));
      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToCompleteErpConnection'), 'completeConnection');
      throw error;
    }
  }

  /**
   * Sync ERP data in the background using existing connection
   * Uses throttling (20 min) and incremental fetching
   * Silent operation - no toast notifications
   * @returns Promise with fetch result or null if connection doesn't exist
   */
  static async syncDataBackground(): Promise<FetchLatestDataResponse | null> {
    try {
      const { data, error } = await supabase.functions.invoke('erp-connection-api', {
        method: 'POST',
        body: {
          action: 'fetchLatestData',
          // No code - will use existing connection
        }
      });

      if (error) {
        // Silent failure for background sync
        return null;
      }

      console.log('Background ERP sync completed:', data);
      return data;
    } catch (error) {
      // Silent failure for background sync
      return null;
    }
  }

  /**
   * Get extension download information
   * Returns metadata about the Business Central extension to install
   * Future: may be personalized per company/customer
   * @returns Promise with extension download URL and metadata
   */
  static async getExtensionDownload(): Promise<ExtensionDownloadResponse> {
    try {
      const { data, error } = await supabase.functions.invoke('erp-connection-api', {
        method: 'POST',
        body: { action: 'getExtensionDownload' }
      });

      if (error) throw error;

      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToGetExtensionDownloadInformation'), 'getExtensionDownload');
      throw error;
    }
  }
}
